import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import type { TaxRegisterEntry, TaxRegisterTotals } from "../types";
import { DTE_TYPE_LABELS, ACCOUNTING_STATUS_LABELS } from "../types";

interface TaxRegisterTableProps {
  entries: TaxRegisterEntry[];
  totals: TaxRegisterTotals | null;
  registerType: "sales" | "purchases";
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
}

function formatCLP(value: number | null): string {
  if (value == null) return "-";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(value);
}

function statusVariant(status: string): "default" | "secondary" | "outline" {
  switch (status) {
    case "posted":
      return "default";
    case "draft":
      return "secondary";
    default:
      return "outline";
  }
}

export function TaxRegisterTable({
  entries,
  totals,
  registerType,
  selectedIds,
  onSelectionChange,
}: TaxRegisterTableProps) {
  if (entries.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No hay registros para este periodo.
        <br />
        Presione <strong>Sincronizar</strong> para obtener datos del SII.
      </div>
    );
  }

  const partyLabel = registerType === "purchases" ? "Proveedor" : "Cliente";
  const pendingEntries = entries.filter((e) => e.accountingStatus === "pending");
  const allPendingSelected =
    pendingEntries.length > 0 && pendingEntries.every((e) => selectedIds.has(e.id));
  const somePendingSelected =
    pendingEntries.some((e) => selectedIds.has(e.id)) && !allPendingSelected;

  const toggleAll = () => {
    if (allPendingSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(pendingEntries.map((e) => e.id)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-3 px-2 w-10">
                <Checkbox
                  checked={allPendingSelected}
                  // @ts-expect-error - indeterminate is valid but not typed
                  indeterminate={somePendingSelected}
                  onCheckedChange={toggleAll}
                  disabled={pendingEntries.length === 0}
                />
              </th>
              <th className="py-3 px-2 font-medium w-20">Tipo</th>
              <th className="py-3 px-2 font-medium w-24">Folio</th>
              <th className="py-3 px-2 font-medium">{partyLabel}</th>
              <th className="py-3 px-2 font-medium w-28">Fecha</th>
              <th className="py-3 px-2 font-medium text-right w-28">Exento</th>
              <th className="py-3 px-2 font-medium text-right w-28">Neto</th>
              <th className="py-3 px-2 font-medium text-right w-28">IVA</th>
              <th className="py-3 px-2 font-medium text-right w-28">Total</th>
              <th className="py-3 px-2 font-medium w-28">Estado</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const isPending = entry.accountingStatus === "pending";
              const isSelected = selectedIds.has(entry.id);

              return (
                <tr
                  key={entry.id}
                  className={`border-b hover:bg-muted/50 ${isSelected ? "bg-primary/5" : ""}`}
                >
                  <td className="py-3 px-2">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleOne(entry.id)}
                      disabled={!isPending}
                    />
                  </td>
                  <td className="py-3 px-2">
                    <div className="font-mono text-xs">{entry.documentTypeCode}</div>
                    <div className="text-xs text-muted-foreground">
                      {DTE_TYPE_LABELS[entry.documentTypeCode] ?? "Otro"}
                    </div>
                  </td>
                  <td className="py-3 px-2 font-mono text-xs">
                    {entry.documentNumber}
                  </td>
                  <td className="py-3 px-2">
                    <div className="font-mono text-xs">{entry.partyTaxId ?? "-"}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {entry.partyName ?? ""}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-xs">
                    {entry.documentDate ?? "-"}
                  </td>
                  <td className="py-3 px-2 text-right font-mono text-xs">
                    {formatCLP(entry.exemptAmount)}
                  </td>
                  <td className="py-3 px-2 text-right font-mono text-xs">
                    {formatCLP(entry.netAmount)}
                  </td>
                  <td className="py-3 px-2 text-right font-mono text-xs">
                    {formatCLP(entry.taxAmount)}
                  </td>
                  <td className="py-3 px-2 text-right font-mono text-xs font-medium">
                    {formatCLP(entry.totalAmount)}
                  </td>
                  <td className="py-3 px-2">
                    <Badge variant={statusVariant(entry.accountingStatus)}>
                      {ACCOUNTING_STATUS_LABELS[entry.accountingStatus] ?? entry.accountingStatus}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary footer */}
      {totals && (
        <div className="flex items-center justify-between border-t pt-3 mt-2 text-sm text-muted-foreground">
          <span>{totals.count} registros</span>
          <div className="flex gap-6">
            <span>Exento: <strong className="text-foreground">{formatCLP(totals.exemptAmount)}</strong></span>
            <span>Neto: <strong className="text-foreground">{formatCLP(totals.netAmount)}</strong></span>
            <span>IVA: <strong className="text-foreground">{formatCLP(totals.taxAmount)}</strong></span>
            <span>Total: <strong className="text-foreground">{formatCLP(totals.totalAmount)}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}
